import React, { useState, useEffect } from 'react';
import {
  collection, query, where, onSnapshot,
  doc, getDoc, setDoc, deleteDoc
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../../Firebaseconfig';
import './CompanyBar.css';

export default function CompanyBar() {
  const [currentUser, setCurrentUser]           = useState(null);
  const [companyProfile, setCompanyProfile]     = useState(null);
  const [orders, setOrders]                     = useState([]);        // still fetched for stats
  const [promoCodes, setPromoCodes]             = useState([]);
  const [loading, setLoading]                   = useState(true);

  // Form state
  const [promoCodeText, setPromoCodeText]   = useState('');
  const [promoType, setPromoType]           = useState('percentage');
  const [promoValue, setPromoValue]         = useState('');
  const [promoMaxUses, setPromoMaxUses]     = useState('');
  const [creatingPromo, setCreatingPromo]   = useState(false);

  /* ── Auth + data subscriptions ── */
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setCurrentUser(null);
        setCompanyProfile(null);
        setOrders([]);
        setPromoCodes([]);
        setLoading(false);
        return;
      }

      setCurrentUser(user);

      // Fetch company profile (one-time)
      getDoc(doc(db, 'users', user.uid))
        .then(snap => { if (snap.exists()) setCompanyProfile(snap.data()); })
        .catch(console.error);

      // Listen to orders (for stats only)
      const ordersQ = query(collection(db, 'orders'), where('companyId', '==', user.uid));
      const unsubOrders = onSnapshot(ordersQ, (snap) => {
        setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      }, (err) => {
        console.error('[CompanyBar] orders:', err);
        setLoading(false);
      });

      // Listen to this restaurant's promo codes
      const promoQ = query(collection(db, 'promo_codes'), where('restaurantId', '==', user.uid));
      const unsubPromo = onSnapshot(promoQ, (snap) => {
        setPromoCodes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      }, (err) => console.error('[CompanyBar] promo_codes:', err));

      return () => {
        unsubOrders();
        unsubPromo();
      };
    });

    return () => unsubAuth();
  }, []);

  /* ── Stats derived from orders ── */
  const avgPrepTime = (() => {
    const preppedOrders = orders.filter(o => o.acceptedAt && o.readyAt);
    if (preppedOrders.length === 0) return '—';
    const times = preppedOrders.map(o => {
      const start = new Date(o.acceptedAt);
      const end = new Date(o.readyAt);
      return (end - start) / 60000;
    }).filter(t => t > 0);
    if (times.length === 0) return '—';
    return (Math.round(times.reduce((sum, t) => sum + t, 0) / times.length * 10) / 10) + 'm';
  })();

  const dailyRevenue = orders
    .filter(o => {
      if (!['completed', 'ready', 'delivering', 'pickingup'].includes(o.status)) return false;
      const date = o.createdAt?.toDate ? o.createdAt.toDate() : new Date(o.createdAt);
      return new Date().toDateString() === date.toDateString();
    })
    .reduce((sum, o) => sum + Number(o.total || 0), 0);

  const rating = Number(companyProfile?.rating || 4.8);
  const stars  = '★'.repeat(Math.round(rating)) + '☆'.repeat(5 - Math.round(rating));

  /* ── Promo code handlers ── */
  const handleCreatePromo = async (e) => {
    e.preventDefault();
    if (!promoCodeText.trim() || !promoValue || !promoMaxUses) {
      alert('Please fill in all fields.');
      return;
    }
    const cleanCode = promoCodeText.trim().toUpperCase();
    if (!/^[A-Z0-9]+$/.test(cleanCode)) {
      alert('Promo code must contain only letters and numbers.');
      return;
    }

    setCreatingPromo(true);
    try {
      const codeRef  = doc(db, 'promo_codes', cleanCode);
      const existing = await getDoc(codeRef);
      if (existing.exists()) {
        alert('A promo code with this name already exists. Choose a unique code.');
        setCreatingPromo(false);
        return;
      }

      const companyName =
        companyProfile?.businessName ||
        `${companyProfile?.firstName || ''} ${companyProfile?.lastName || ''}`.trim() ||
        'Restaurant';

      await setDoc(codeRef, {
        code:         cleanCode,
        type:         promoType,
        value:        parseFloat(promoValue),
        scope:        'restaurant',
        restaurantId: currentUser.uid,
        companyName,
        maxUses:      parseInt(promoMaxUses, 10),
        usedCount:    0,
        redeemedBy:   [],
        active:       true,
        createdAt:    new Date().toISOString(),
        createdBy:    currentUser.uid,
      });

      setPromoCodeText('');
      setPromoValue('');
      setPromoMaxUses('');
      alert(`Promo code ${cleanCode} created!`);
    } catch (err) {
      console.error('[CompanyBar] create promo:', err);
      alert('Failed to create promo code: ' + err.message);
    } finally {
      setCreatingPromo(false);
    }
  };

  const handleDeletePromo = async (codeId) => {
    if (!window.confirm(`Delete promo code "${codeId}"? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, 'promo_codes', codeId));
    } catch (err) {
      console.error('[CompanyBar] delete promo:', err);
      alert('Failed to delete: ' + err.message);
    }
  };

  /* ── Render ── */
  return (
    <div className="company-dashboard">

      {/* ── Header ── */}
      <div className="cb-header-row">
        <div>
          <h1 className="cb-title">Restaurant Dashboard</h1>
          <p className="cb-subtitle">Manage your promo codes and track performance.</p>
        </div>
      </div>

      {/* ── Stats row ── */}
      <div className="cb-stats-row" style={{ marginBottom: 36 }}>
        <div className="cb-stat-card">
          <div className="cb-stat-lbl">AVG. PREP TIME</div>
          <div className="cb-stat-val">{avgPrepTime}</div>
        </div>
        <div className="cb-stat-card">
          <div className="cb-stat-lbl">DAILY REVENUE</div>
          <div className="cb-stat-val">${dailyRevenue.toFixed(2)}</div>
        </div>
        <div className="cb-stat-card">
          <div className="cb-stat-lbl">TOTAL ORDERS</div>
          <div className="cb-stat-val">{orders.length}</div>
        </div>
        <div className="cb-stat-card">
          <div className="cb-stat-lbl">MERCHANT RATING</div>
          <div className="cb-stat-val">
            {rating.toFixed(1)} <span className="cb-stars">{stars}</span>
          </div>
        </div>
      </div>

      {/* ── Promo Codes Panel ── */}
      <div className="cb-promo-panel">
        <div className="cb-promo-panel-header">
          <div>
            <h2 className="cb-promo-title">Promo Codes</h2>
            <p className="cb-promo-subtitle">
              Create discount codes that apply only to your restaurant's orders.
            </p>
          </div>
        </div>

        <div className="cb-promo-layout">
          {/* Create form */}
          <form onSubmit={handleCreatePromo} className="cb-promo-form">
            <h3 className="cb-promo-form-title">Create New Code</h3>

            <div className="cb-form-group">
              <label htmlFor="cbPromoCode">Code Name</label>
              <input
                id="cbPromoCode"
                type="text"
                placeholder="e.g. BURGER20"
                value={promoCodeText}
                onChange={e => setPromoCodeText(e.target.value.toUpperCase())}
                required
              />
            </div>

            <div className="cb-form-group">
              <label htmlFor="cbPromoType">Discount Type</label>
              <select
                id="cbPromoType"
                value={promoType}
                onChange={e => setPromoType(e.target.value)}
              >
                <option value="percentage">Percentage (%)</option>
                <option value="value">Fixed Amount ($)</option>
              </select>
            </div>

            <div className="cb-form-group">
              <label htmlFor="cbPromoValue">
                Value ({promoType === 'percentage' ? '%' : '$'})
              </label>
              <input
                id="cbPromoValue"
                type="number"
                placeholder="e.g. 20"
                value={promoValue}
                onChange={e => setPromoValue(e.target.value)}
                min="0.01"
                step="any"
                required
              />
            </div>

            <div className="cb-form-group">
              <label htmlFor="cbPromoUses">Max Uses</label>
              <input
                id="cbPromoUses"
                type="number"
                placeholder="e.g. 50"
                value={promoMaxUses}
                onChange={e => setPromoMaxUses(e.target.value)}
                min="1"
                required
              />
            </div>

            <button type="submit" disabled={creatingPromo} className="cb-promo-submit-btn">
              {creatingPromo ? 'Creating…' : '+ Create Promo Code'}
            </button>
          </form>

          {/* Active codes list */}
          <div className="cb-promo-list">
            <h3 className="cb-promo-form-title">
              Active Codes
              <span className="cb-promo-count">{promoCodes.length}</span>
            </h3>

            {loading ? (
              <p className="cb-promo-empty">Loading…</p>
            ) : promoCodes.length === 0 ? (
              <div className="cb-promo-empty-state">
                <span className="cb-promo-empty-icon">🏷️</span>
                <p>No promo codes yet. Create your first one!</p>
              </div>
            ) : (
              <div className="cb-promo-table-wrapper">
                <table className="cb-promo-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Type</th>
                      <th>Discount</th>
                      <th>Used / Max</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {promoCodes.map(code => (
                      <tr key={code.id}>
                        <td className="cb-code-cell">{code.code}</td>
                        <td>{code.type === 'percentage' ? 'Percent' : 'Fixed'}</td>
                        <td>
                          {code.type === 'percentage'
                            ? `${code.value}%`
                            : `$${parseFloat(code.value).toFixed(2)}`}
                        </td>
                        <td>
                          <span className="cb-usage-bar-wrap">
                            <span className="cb-usage-text">
                              {code.usedCount} / {code.maxUses}
                            </span>
                            <span className="cb-usage-bar">
                              <span
                                className="cb-usage-bar-fill"
                                style={{
                                  width: `${Math.min(100, (code.usedCount / code.maxUses) * 100)}%`,
                                  background: code.usedCount >= code.maxUses ? '#ef4444' : '#f97316'
                                }}
                              />
                            </span>
                          </span>
                        </td>
                        <td>
                          <span className={`cb-code-status ${code.active && code.usedCount < code.maxUses ? 'active' : 'exhausted'}`}>
                            {code.active && code.usedCount < code.maxUses ? 'Active' : 'Exhausted'}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            onClick={() => handleDeletePromo(code.id)}
                            className="cb-delete-code-btn"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
